import { useCallback, useState } from 'react';
import type { ReactEventHandler } from 'react';

interface CimageFallbackState {
  renderedSrc: string;
  sourceSrc: string;
}

interface CimageResourceStateInput {
  fallbackSrc?: string;
  onError?: ReactEventHandler<HTMLImageElement>;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  src: string;
}

interface CimageResourceState {
  handleError: ReactEventHandler<HTMLImageElement>;
  handleLoad: ReactEventHandler<HTMLImageElement>;
  isLoaded: boolean;
  renderedSrc: string;
  setImageRef: (img: HTMLImageElement | null) => void;
}

function getRenderedSrc(input: {
  fallbackState: CimageFallbackState | null;
  src: string;
}): string {
  return input.fallbackState?.sourceSrc === input.src
    ? input.fallbackState.renderedSrc
    : input.src;
}

export function useCimageResourceState({
  fallbackSrc,
  onError,
  onLoad,
  src,
}: CimageResourceStateInput): CimageResourceState {
  const [fallbackState, setFallbackState] =
    useState<CimageFallbackState | null>(null);
  const renderedSrc = getRenderedSrc({ fallbackState, src });
  const hasTriedFallback = fallbackState?.sourceSrc === src;
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoaded = loadedSrc === renderedSrc;

  // Cached images don't always fire onLoad. The ref callback runs in commit,
  // so it can mark already-complete images without a prop-change effect.
  const setImageRef = useCallback((img: HTMLImageElement | null): void => {
    if (img?.complete && img.naturalWidth > 0) {
      setLoadedSrc(renderedSrc);
    }
  }, [renderedSrc]);

  const handleLoad = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      setLoadedSrc(renderedSrc);
      onLoad?.(event);
    },
    [onLoad, renderedSrc],
  );

  const handleError = useCallback<ReactEventHandler<HTMLImageElement>>(
    (event) => {
      onError?.(event);

      if (fallbackSrc && renderedSrc !== fallbackSrc && !hasTriedFallback) {
        setFallbackState({ renderedSrc: fallbackSrc, sourceSrc: src });
        setLoadedSrc(null);
        return;
      }

      setLoadedSrc(renderedSrc);
    },
    [fallbackSrc, hasTriedFallback, onError, renderedSrc, src],
  );

  return {
    handleError,
    handleLoad,
    isLoaded,
    renderedSrc,
    setImageRef,
  };
}
